import React, { useState, useEffect } from "react";
import { FaEllipsisH, FaPlus, FaPercent, FaBox, FaSearch, FaUser } from "react-icons/fa";
import { Tag, Package, Percent, Plus, Search } from "lucide-react";
import billingService from "../../../services/billingService";
import { organizationApiService } from "../../../services/apiV2Service";
import { getCurrentUserOrganizationId } from "../host-clients/utils/hostClientsUtils";
import { showApiMessage, getApiMessage } from "../../../utils/messageHandler";
import moment from "moment";
import { Button } from "../../../shadcn/ui/button";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import { Textarea } from "../../../shadcn/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import { Checkbox } from "../../../shadcn/ui/checkbox";
import { Badge } from "../../../shadcn/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../shadcn/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../shadcn/ui/table";
import logger from "../../../utils/logger";
import * as localService from "../../../services/localService";
import HConfirmDialog from "../../../hcomponents/HConfirmDialog";
import HActionButton from "../../../hcomponents/HActionButton";
import HEmptyState from "../../../hcomponents/HEmptyState";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import HPagination from "../../../hcomponents/HPagination";
import ViewToggle from "../host-spaces/components/ViewToggle";
import { useCurrencies } from "../../../hooks/useCurrencies";
import { useUserCurrency } from "../../../hooks/useUserCurrency";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import Pagination from "../../../components/Pagination";

const OffersDiscounts = () => {
  // Get current user for feature restrictions
  const currentUser = localService.getUser();
  const _orgTypeCode = currentUser?.organization?.type?.code || currentUser?.organization?.typeCode || "";

  // Use centralized feature access control
  const canCreateItems = FeatureAccess.canCreateOffersDiscounts(currentUser);

  // Use cached currencies from Redux
  const { currencies } = useCurrencies();
  const userCurrencyCode = useUserCurrency();
  const userCurrency = currencies.find(c => c.code === userCurrencyCode);

  // State for discounts
  const [discounts, setDiscounts] = useState([]);
  const [newDiscount, setNewDiscount] = useState({
    code: "",
    name: "",
    description: "",
    active: true,
    percentage: null,
    fixedValue: null,
    startDate: "",
    endDate: "",
  });

  // State for offers
  const [offers, setOffers] = useState([]);
  const [newOffer, setNewOffer] = useState({
    code: "",
    name: "",
    description: "",
    organizationId: null,
    providerId: null,
    serviceSubcategoryId: null,
    durationUnit: "HOUR",
    minimumDuration: null,
    maximumDuration: null,
    maximumCheckIns: null,
    numberOfServiceProviders: 1,
    serviceTier: "HIGH",
    currencyId: null, // REQUIRED - price currency
    unitPrice: 0, // REQUIRED - price amount
    packageIds: [],
  });

  // Common state
  const [activeTab, setActiveTab] = useState("discounts");
  const [viewMode, setViewMode] = useState("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [_errorMessage, setErrorMessage] = useState("");
  const [_successMessage, setSuccessMessage] = useState("");
  const [showCreateDiscountModal, setShowCreateDiscountModal] = useState(false);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [_isLoadingOfferDetails, setIsLoadingOfferDetails] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packagesError, setPackagesError] = useState(null);
  const [packageCurrentPage, setPackageCurrentPage] = useState(1);
  const [packagePageSize] = useState(50);
  const [packageTotalCount, setPackageTotalCount] = useState(0);

  // Provider selection state
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Service categories and subcategories for offers
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceSubcategories, setServiceSubcategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // Form validation errors
  const [offerFormErrors, setOfferFormErrors] = useState({});

  // Confirm dialog states
  const [showDeleteDiscountDialog, setShowDeleteDiscountDialog] = useState(false);
  const [showDeleteOfferDialog, setShowDeleteOfferDialog] = useState(false);
  const [showDeletePackageDialog, setShowDeletePackageDialog] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState(null);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [packageToDelete, setPackageToDelete] = useState(null);

  // Package state
  const [showCreatePackageModal, setShowCreatePackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [originalPackageData, setOriginalPackageData] = useState(null); // Store original package for comparison
  const [newPackage, setNewPackage] = useState({
    code: "",
    name: "",
    description: "",
    type: "CUSTOM",
    version: "1.0.0",
    discountId: "",
    maximumServiceProvidersAllowed: 1,
    maximumTeamSize: 1,
    currencyId: "",
    validity: "",
    validityTimeUnit: "MONTH",
    expiryDate: "",
    recurring: true,
    offerIds: [],
    orgTypeIds: [],
    customerOrgIds: [],
    providerId: 0,
  });

  // Available offers for package creation
  const [availableOffers, setAvailableOffers] = useState([]);
  const [offerCurrentPage, setOfferCurrentPage] = useState(1);
  const [offerPageSize] = useState(50);
  const [offerTotalCount, setOfferTotalCount] = useState(0);

  // Customer organization search state (for CUSTOM packages)
  const [customerOrgSearchQuery, setCustomerOrgSearchQuery] = useState("");
  const [customerOrgSearchResults, setCustomerOrgSearchResults] = useState([]);
  const [isSearchingCustomerOrgs, setIsSearchingCustomerOrgs] = useState(false);
  const [showCustomerOrgSearch, setShowCustomerOrgSearch] = useState(false);

  // Offer search for packages
  const [offerSearchQuery, setOfferSearchQuery] = useState("");

  // Note: Hosts do NOT need provider selection state for packages
  // Provider is automatically the logged-in user's organization

  // Auto-set currency when user currency is loaded
  useEffect(() => {
    if (userCurrency && !newOffer.currencyId) {
      setNewOffer(prev => ({ ...prev, currencyId: userCurrency.id }));
    }
  }, [userCurrency, newOffer.currencyId]);

  // Fetch initial data on component mount
  useEffect(() => {
    fetchDiscounts();
    fetchOffers();
    fetchPackages();
    fetchServiceCategories();
  }, []);

  const fetchPackageDependencies = async (page = 1) => {
    try {
      // Get current user's organization
      const user = localService.getUser();
      const userOrgId = user?.organizationId || user?.organization?.id;

      // Load offers for package creation - pass providerId to get ONLY host's offers (no universal)
      const offersResponse = await billingService.getOffers({
        providerId: userOrgId,
        page: page,
        limit: offerPageSize,
        type: "BUNDLE",
      });
      if (offersResponse.data?.data) {
        setAvailableOffers(offersResponse.data.data);

        // Extract pagination info
        const pagination = offersResponse.data.pagination || {};
        const totalCount = parseInt(
          pagination.totalCount || pagination.total || offersResponse.data.data.length || 0,
          10
        );
        setOfferTotalCount(isNaN(totalCount) ? 0 : totalCount);
      }

      // Note: Hosts don't need organization types - the /me endpoint doesn't support orgTypeIds
    } catch (error) {
      logger.error("Error loading package dependencies:", error);
    }
  };

  const handlePackageInputChange = (field, value) => {
    setNewPackage(prev => ({ ...prev, [field]: value }));
  };

  // Note: Hosts do NOT have provider selection for packages
  // Provider is automatically set to the logged-in user's organization by the /me endpoint

  // Offer Search Function for Packages
  const searchOffersForPackage = async (query, page = 1) => {
    if (!query.trim()) {
      // If no query, reload default offers
      setOfferCurrentPage(page);
      await fetchPackageDependencies(page);
      return;
    }

    try {
      // Get current user's organization
      const user = localService.getUser();
      const userOrgId = user?.organizationId || user?.organization?.id;

      const searchParams = {
        q: query,
        limit: offerPageSize,
        page: page,
        providerId: userOrgId, // Pass providerId to get host's offers + universal
        type: "BUNDLE",
      };

      const searchResponse = await billingService.searchOffers(searchParams);
      if (searchResponse.data?.data) {
        setAvailableOffers(searchResponse.data.data);

        // Extract pagination info
        const pagination = searchResponse.data.pagination || {};
        const totalCount = parseInt(
          pagination.totalCount || pagination.total || searchResponse.data.data.length || 0,
          10
        );
        setOfferTotalCount(isNaN(totalCount) ? 0 : totalCount);
      }
    } catch (error) {
      logger.error("Error searching offers:", error);
      setErrorMessage(getApiMessage(error, "search offers"));
    }
  };

  // Customer Organization Search Functions (for CUSTOM packages)
  const searchCustomerOrganizations = async query => {
    if (!query.trim()) {
      setCustomerOrgSearchResults([]);
      return;
    }

    try {
      setIsSearchingCustomerOrgs(true);
      const searchParams = {
        q: query,
        limit: 20,
        page: 1,
      };

      const searchResponse = await organizationApiService.searchOrganizations(searchParams);
      if (searchResponse.data?.data) {
        setCustomerOrgSearchResults(searchResponse.data.data);
      }
    } catch (error) {
      logger.error("Error searching customer organizations:", error);
      setErrorMessage(getApiMessage(error, "search organizations"));
    } finally {
      setIsSearchingCustomerOrgs(false);
    }
  };

  const addCustomerOrganization = org => {
    const orgId = org.id;
    if (!newPackage.customerOrgIds.includes(orgId.toString())) {
      setNewPackage({
        ...newPackage,
        customerOrgIds: [...newPackage.customerOrgIds, orgId.toString()],
      });
    }
    setCustomerOrgSearchQuery("");
    setCustomerOrgSearchResults([]);
    setShowCustomerOrgSearch(false);
  };

  const removeCustomerOrganization = orgId => {
    setNewPackage({
      ...newPackage,
      customerOrgIds: newPackage.customerOrgIds.filter(id => id !== orgId.toString()),
    });
  };

  // Organization search for provider selection (offers/discounts)
  const searchOrganizations = async searchTerm => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const currentOrgId = getCurrentUserOrganizationId();
      const response = await organizationApiService.searchOrganizations({
        q: searchTerm,
        limit: 10,
      });

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Filter out current user's organization in UI
        const filteredResults = response.data.data.filter(org => org.id !== currentOrgId);
        setSearchResults(filteredResults);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      logger.error("Error searching organizations:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleOrganizationSelection = organization => {
    setSelectedOrganization(organization);
    setSelectedProviderId(organization.id);

    // Update organizationId and providerId in forms based on selected organization
    setNewDiscount(prev => ({ ...prev, organizationId: organization.id }));
    setNewOffer(prev => ({ ...prev, organizationId: organization.id, providerId: organization.id }));

    // Clear search
    setOrganizationSearchTerm("");
    setSearchResults([]);
  };

  const fetchServiceCategories = async () => {
    try {
      logger.log("Fetching service categories...");
      const response = await billingService.getPublicServiceCategories();
      logger.log("Categories response:", response);

      if (response.data && Array.isArray(response.data)) {
        logger.log("Setting categories from response.data:", response.data);
        setServiceCategories(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        logger.log("Setting categories from response.data.data:", response.data.data);
        setServiceCategories(response.data.data);
      } else {
        logger.log("No valid categories data found in response");
        setServiceCategories([]);
      }
    } catch (error) {
      logger.error("Error fetching service categories:", error);
      logger.error("Error fetching service categories:", error);
    }
  };

  const fetchServiceSubcategories = async categoryId => {
    try {
      logger.log("Fetching service subcategories for categoryId:", categoryId);
      const response = await billingService.getServiceSubcategories(categoryId);
      logger.log("Subcategories response:", response);

      if (response.data && Array.isArray(response.data)) {
        logger.log("Setting subcategories from response.data:", response.data);
        setServiceSubcategories(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        logger.log("Setting subcategories from response.data.data:", response.data.data);
        setServiceSubcategories(response.data.data);
      } else {
        logger.log("No valid subcategories data found in response");
        setServiceSubcategories([]);
      }
    } catch (error) {
      logger.error("Error fetching service subcategories:", error);
      logger.error("Error fetching service subcategories:", error);
    }
  };

  const fetchDiscounts = async () => {
    try {
      setIsLoading(true);
      const response = await billingService.getDiscounts();
      if (response.data) {
        let discountsList = Array.isArray(response.data.data) ? response.data.data : [];

        // Use centralized filtering
        discountsList = FeatureAccess.filterDiscountsForRole(discountsList, currentUser);

        setDiscounts(discountsList);
      }
    } catch (error) {
      logger.error("Error fetching discounts:", error);
      setErrorMessage("Failed to fetch discounts");
      showApiMessage(error, "fetch discounts");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOffers = async () => {
    try {
      // Get current user's organization and pass as providerId
      const user = localService.getUser();
      const userOrgId = user?.organizationId || user?.organization?.id;

      const response = await billingService.getOffers({ providerId: userOrgId, type: "BUNDLE" });
      if (response.data) {
        let offersList = Array.isArray(response.data.data) ? response.data.data : [];

        // Use centralized filtering
        offersList = FeatureAccess.filterOffersForRole(offersList, currentUser);

        setOffers(offersList);
      }
    } catch (error) {
      logger.error("Error fetching offers:", error);
      setErrorMessage("Failed to fetch offers");
      showApiMessage(error, "fetch offers");
    }
  };

  const fetchPackages = async (page = 1) => {
    try {
      setPackagesLoading(true);
      setPackagesError(null);
      const response = await billingService.getPackages({
        page: page,
        limit: packagePageSize,
      });
      if (response.data) {
        let packagesList = Array.isArray(response.data.data) ? response.data.data : [];

        // Use centralized filtering for restricted roles
        if (!canCreateItems) {
          packagesList = FeatureAccess.filterPackagesForRole(packagesList, currentUser);
        }

        setPackages(packagesList);
        setPackagesError(packagesList.length === 0 ? "No packages available" : null);

        // Extract pagination info
        const pagination = response.data.pagination || {};
        const totalCount = parseInt(pagination.totalCount || pagination.total || packagesList.length || 0, 10);
        setPackageTotalCount(isNaN(totalCount) ? 0 : totalCount);
      }
    } catch (error) {
      logger.error("Error fetching packages:", error);
      setPackagesError("Failed to load packages");
    } finally {
      setPackagesLoading(false);
    }
  };

  // Get available offers and discounts for package creation
  const _getAvailableOffers = () => {
    return offers.filter(offer => offer.active !== false);
  };

  const getAvailableDiscounts = () => {
    return discounts.filter(discount => discount.active !== false);
  };

  const handleDiscountInputChange = (field, value) => {
    setNewDiscount(prev => ({ ...prev, [field]: value }));
  };

  const handleOfferInputChange = (field, value) => {
    setNewOffer(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateDiscount = async e => {
    e.preventDefault();
    try {
      setIsLoading(true);

      // Validate organization selection - REQUIRED for discounts
      if (!newDiscount.organizationId) {
        setErrorMessage("Please select a provider organization for this discount");
        throw new Error("Please select a provider organization for this discount");
      }

      // Convert percentage to decimal (e.g., 20% -> 0.2) - MATCH MAIN SCREEN LOGIC
      const discountPayload = {
        ...newDiscount,
        percentage: newDiscount.percentage ? newDiscount.percentage / 100 : undefined,
      };

      const response = await billingService.createDiscount(discountPayload);
      showApiMessage(response, "create discount");
      setShowCreateDiscountModal(false);
      fetchDiscounts();
      resetDiscountForm();
    } catch (error) {
      logger.error("Error creating discount:", error);
      setErrorMessage(getApiMessage(error, "create discount"));
      showApiMessage(error, "create discount");
    } finally {
      setIsLoading(false);
    }
  };

  const resetDiscountForm = () => {
    setNewDiscount({
      code: "",
      name: "",
      description: "",
      active: true,
      percentage: null,
      fixedValue: null,
      startDate: "",
      endDate: "",
      organizationId: null,
    });
    setSelectedProviderId(null);
    setSelectedOrganization(null);
    setOrganizationSearchTerm("");
    setSearchResults([]);
  };

  const handleCreateOffer = async e => {
    e.preventDefault();
    logger.log("🚀 handleCreateOffer called");
    logger.log("🚀 Current newOffer state:", newOffer);

    // Clear previous form errors
    setOfferFormErrors({});

    // Validate form fields
    const errors = {};
    // organizationId is optional - if not provided, offer will be unlinked
    if (!newOffer.serviceSubcategoryId) {
      errors.serviceSubcategoryId = "Please select a service subcategory for this offer";
    }
    if (!newOffer.currencyId) {
      errors.currencyId = "Please select a currency for this offer";
    }
    if (!newOffer.unitPrice || newOffer.unitPrice <= 0) {
      errors.unitPrice = "Please enter a valid unit price greater than 0";
    }

    logger.log("🚀 Validation errors:", errors);

    // If there are validation errors, show them and don't submit
    if (Object.keys(errors).length > 0) {
      logger.log("🚀 Validation failed, showing errors");
      setOfferFormErrors(errors);
      return;
    }

    logger.log("🚀 Validation passed, proceeding with API call");

    try {
      setIsLoading(true);

      const offerPayload = {
        ...newOffer,
        providerId: newOffer.providerId && newOffer.providerId !== 0 ? newOffer.providerId : null,
      };

      logger.log("Creating offer with payload:", offerPayload);

      const response = await billingService.createOffer(offerPayload);
      showApiMessage(response, "create offer");
      setShowCreateOfferModal(false);
      fetchOffers();
      resetOfferForm();
    } catch (error) {
      logger.error("Error creating offer:", error);
      logger.error("Error creating offer:", error);
      setErrorMessage(getApiMessage(error, "create offer"));
      showApiMessage(error, "create offer");
    } finally {
      setIsLoading(false);
    }
  };

  const resetOfferForm = () => {
    setNewOffer({
      code: "",
      name: "",
      description: "",
      organizationId: null,
      providerId: null,
      serviceSubcategoryId: null,
      durationUnit: "HOUR",
      minimumDuration: null,
      maximumDuration: null,
      maximumCheckIns: null,
      numberOfServiceProviders: 1,
      serviceTier: "HIGH",
      currencyId: userCurrency?.id || null, // Auto-set to user's currency
      unitPrice: 0, // REQUIRED - price amount
      packageIds: [],
    });
    setSelectedProviderId(null);
    setSelectedCategoryId(null);
    setServiceSubcategories([]);
    setSelectedOrganization(null);
    setOrganizationSearchTerm("");
    setSearchResults([]);
    setOfferFormErrors({});
  };

  const handleEditDiscount = discount => {
    setEditingDiscount(discount);
    setNewDiscount({
      ...discount,
      // Convert decimal percentage back to whole number for form display - MATCH MAIN SCREEN LOGIC
      percentage: discount.percentage ? Math.round(discount.percentage * 100) : undefined,
      startDate: discount.startDate ? moment(discount.startDate).format("YYYY-MM-DD") : "",
      endDate: discount.endDate ? moment(discount.endDate).format("YYYY-MM-DD") : "",
      organizationId: discount.organizationId,
    });
    setSelectedProviderId(discount.organizationId);
    // Set selected organization for display if available
    if (discount.organization) {
      setSelectedOrganization(discount.organization);
    }
    setShowCreateDiscountModal(true);
  };

  const handleEditOffer = async offer => {
    try {
      // Show the edit form with loading state
      setShowCreateOfferModal(true);
      setIsLoadingOfferDetails(true);

      // Fetch complete offer details from API
      const response = await billingService.getOfferById(offer.id);
      const fullOffer = response.data?.data || offer;

      logger.log("Full offer data from API:", fullOffer);

      // Extract package IDs from the packages array if it exists
      let packageIds = [];
      if (fullOffer.packages && Array.isArray(fullOffer.packages)) {
        packageIds = fullOffer.packages.map(pkg => pkg.id);
      } else if (fullOffer.packageIds && Array.isArray(fullOffer.packageIds)) {
        packageIds = fullOffer.packageIds;
      }

      setEditingOffer(fullOffer);
      setNewOffer({
        ...fullOffer,
        packageIds: packageIds,
        // keep providerId for PATCH; do not persist organizationId in form state
        providerId: fullOffer.providerId ?? fullOffer.organizationId ?? null,
      });

      // Set the selected provider ID (prefer providerId)
      setSelectedProviderId(fullOffer.providerId ?? fullOffer.organizationId ?? null);

      // If provider organization is present, set it for display
      if (fullOffer.provider || fullOffer.organization) {
        setSelectedOrganization(fullOffer.provider || fullOffer.organization);
      } else {
        setSelectedOrganization(null);
      }

      // Set the selected category based on the offer's subcategory
      if (fullOffer.serviceSubcategoryId) {
        // First try to find in existing subcategories
        let subcategory = serviceSubcategories.find(sub => sub.id === fullOffer.serviceSubcategoryId);

        if (subcategory) {
          setSelectedCategoryId(subcategory.categoryId);
          fetchServiceSubcategories(subcategory.categoryId);
        } else {
          // If not found, we need to fetch subcategories for all categories to find the right one
          logger.log("Subcategory not found in existing data, fetching all subcategories...");
          Promise.all(
            serviceCategories.map(cat =>
              billingService
                .getServiceSubcategories(cat.id)
                .then(response => {
                  const subs =
                    response.data && Array.isArray(response.data)
                      ? response.data
                      : response.data && response.data.data && Array.isArray(response.data.data)
                        ? response.data.data
                        : [];
                  return { categoryId: cat.id, subcategories: subs };
                })
                .catch(() => ({ categoryId: cat.id, subcategories: [] }))
            )
          ).then(results => {
            for (const result of results) {
              const foundSub = result.subcategories.find(sub => sub.id === fullOffer.serviceSubcategoryId);
              if (foundSub) {
                logger.log("Found subcategory in category:", result.categoryId);
                setSelectedCategoryId(result.categoryId);
                setServiceSubcategories(result.subcategories);
                break;
              }
            }
          });
        }
      } else {
        // Clear category selection if no subcategory
        setSelectedCategoryId(null);
        setServiceSubcategories([]);
      }

      // Fetch packages for linking to this offer
      fetchPackages();

      // Hide loading state after data is loaded
      setIsLoadingOfferDetails(false);
    } catch (error) {
      logger.error("Error fetching offer details:", error);
      // Fallback to using the offer data from the table
      setEditingOffer(offer);
      setNewOffer({
        ...offer,
        packageIds: offer.packageIds || [],
      });
      setIsLoadingOfferDetails(false);
    }
  };

  const handleUpdateDiscount = async e => {
    e.preventDefault();
    try {
      setIsLoading(true);

      // Validate organization selection - REQUIRED for discounts
      if (!newDiscount.organizationId) {
        setErrorMessage("Please select a provider organization for this discount");
        throw new Error("Please select a provider organization for this discount");
      }

      // Only send the fields that are allowed for updates
      // Convert percentage to decimal (e.g., 20% -> 0.2) - MATCH MAIN SCREEN LOGIC
      const discountPayload = {
        code: newDiscount.code,
        name: newDiscount.name,
        description: newDiscount.description,
        percentage: newDiscount.percentage ? newDiscount.percentage / 100 : undefined,
        fixedValue: newDiscount.fixedValue,
        startDate: newDiscount.startDate,
        endDate: newDiscount.endDate,
        active: newDiscount.active,
      };

      logger.log("Update discount payload:", discountPayload);

      const response = await billingService.updateDiscount(editingDiscount.id, discountPayload);
      showApiMessage(response, "update discount");
      setShowCreateDiscountModal(false);
      setEditingDiscount(null);
      fetchDiscounts();
      resetDiscountForm();
    } catch (error) {
      logger.error("Error updating discount:", error);
      logger.error("Error updating discount:", error);
      setErrorMessage(getApiMessage(error, "update discount"));
      showApiMessage(error, "update discount");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateOffer = async e => {
    e.preventDefault();

    // Clear previous form errors
    setOfferFormErrors({});

    // Validate form fields
    const errors = {};
    // organizationId is optional - if not provided, offer will be unlinked
    if (!newOffer.serviceSubcategoryId) {
      errors.serviceSubcategoryId = "Please select a service subcategory for this offer";
    }
    if (!newOffer.currencyId) {
      errors.currencyId = "Please select a currency for this offer";
    }
    if (!newOffer.unitPrice || newOffer.unitPrice <= 0) {
      errors.unitPrice = "Please enter a valid unit price greater than 0";
    }

    // If there are validation errors, show them and don't submit
    if (Object.keys(errors).length > 0) {
      setOfferFormErrors(errors);
      return;
    }

    try {
      setIsLoading(true);

      // Only send the fields that are allowed for updates
      const offerPayload = {
        code: newOffer.code,
        name: newOffer.name,
        description: newOffer.description,
        serviceSubcategoryId: newOffer.serviceSubcategoryId,
        durationUnit: newOffer.durationUnit,
        minimumDuration: newOffer.minimumDuration,
        maximumDuration: newOffer.maximumDuration,
        maximumCheckIns: newOffer.maximumCheckIns,
        numberOfServiceProviders: newOffer.numberOfServiceProviders,
        serviceTier: newOffer.serviceTier,
        currencyId: newOffer.currencyId, // Price field
        unitPrice: newOffer.unitPrice, // Price field
        // Do NOT send organizationId in PATCH; only providerId is allowed
        providerId: newOffer.providerId && newOffer.providerId !== 0 ? newOffer.providerId : null,
      };

      // Handle package associations using the correct API fields
      if (editingOffer && editingOffer.packageIds) {
        // Get the original package IDs from the offer being edited
        const originalPackageIds = editingOffer.packageIds || [];
        const newPackageIds = newOffer.packageIds || [];

        // Find packages to link (new ones that weren't in the original)
        const linkedPackageIds = newPackageIds.filter(id => !originalPackageIds.includes(id));

        // Find packages to unlink (original ones that aren't in the new selection)
        const unlinkedPackageIds = originalPackageIds.filter(id => !newPackageIds.includes(id));

        if (linkedPackageIds.length > 0) {
          offerPayload.linkedPackageIds = linkedPackageIds;
        }

        if (unlinkedPackageIds.length > 0) {
          offerPayload.unlinkedPackageIds = unlinkedPackageIds;
        }
      } else if (newOffer.packageIds && newOffer.packageIds.length > 0) {
        // If this is a new offer or no original packages, just link the selected packages
        offerPayload.linkedPackageIds = newOffer.packageIds;
      }

      logger.log("Update offer payload:", offerPayload);

      const response = await billingService.updateOffer(editingOffer.id, offerPayload);
      showApiMessage(response, "update offer");
      setShowCreateOfferModal(false);
      setEditingOffer(null);
      fetchOffers();
      resetOfferForm();
    } catch (error) {
      logger.error("Error updating offer:", error);
      logger.error("Error updating offer:", error);
      setErrorMessage(getApiMessage(error, "update offer"));
      showApiMessage(error, "update offer");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDiscount = async discountId => {
    const discount = discounts.find(d => d.id === discountId);
    setDiscountToDelete(discount);
    setShowDeleteDiscountDialog(true);
  };

  const handleDeleteOffer = async offerId => {
    const offer = offers.find(o => o.id === offerId);
    setOfferToDelete(offer);
    setShowDeleteOfferDialog(true);
  };

  const confirmDeleteDiscount = async () => {
    if (!discountToDelete) return;

    try {
      setIsLoading(true);
      const response = await billingService.deleteDiscount(discountToDelete.id);
      setSuccessMessage("Discount deleted successfully!");
      showApiMessage(response, "delete discount");
      fetchDiscounts();
    } catch (error) {
      logger.error("Error deleting discount:", error);
      setErrorMessage("Failed to delete discount");
      showApiMessage(error, "delete discount");
    } finally {
      setIsLoading(false);
      setShowDeleteDiscountDialog(false);
      setDiscountToDelete(null);
    }
  };

  const confirmDeleteOffer = async () => {
    if (!offerToDelete) return;

    try {
      setIsLoading(true);
      const response = await billingService.deleteOffer(offerToDelete.id);
      setSuccessMessage("Offer deleted successfully!");
      showApiMessage(response, "delete offer");
      fetchOffers();
    } catch (error) {
      logger.error("Error deleting offer:", error);
      setErrorMessage("Failed to delete offer");
      showApiMessage(error, "delete offer");
    } finally {
      setIsLoading(false);
      setShowDeleteOfferDialog(false);
      setOfferToDelete(null);
    }
  };

  const handleCreatePackage = async e => {
    e.preventDefault();
    try {
      setIsLoading(true);

      // Validate required fields - MATCH MAIN SCREEN LOGIC
      if (!newPackage.currencyId || newPackage.currencyId === "") {
        setErrorMessage("Please select a currency");
        throw new Error("Please select a currency");
      }

      // Format expiryDate to YYYY-MM-DD if provided
      const formattedExpiryDate = newPackage.expiryDate
        ? new Date(newPackage.expiryDate).toISOString().split("T")[0]
        : undefined;

      // Use /me endpoint payload - EXACTLY MATCH SWAGGER SPEC
      const packagePayload = {
        // REQUIRED fields per Swagger
        name: newPackage.name,
        code: newPackage.code,
        description: newPackage.description,
        currencyId: parseInt(newPackage.currencyId),
        type: "CUSTOM",
        version: newPackage.version,
        maximumServiceProvidersAllowed: newPackage.maximumServiceProvidersAllowed,
        maximumTeamSize: newPackage.maximumTeamSize,
        recurring: newPackage.recurring,
        offerIds: newPackage.offerIds.map(id => parseInt(id)), // REQUIRED array (can be empty)
        // OPTIONAL fields per Swagger
        discountId: newPackage.discountId ? parseInt(newPackage.discountId) : null,
        validity: newPackage.recurring ? (newPackage.validity ? parseInt(newPackage.validity) : null) : null,
        validityTimeUnit: newPackage.recurring ? newPackage.validityTimeUnit || null : null,
        expiryDate: formattedExpiryDate || null,
        customerOrgIds: newPackage.customerOrgIds?.map(id => parseInt(id)) || [],
      };

      logger.log("Creating package with payload:", packagePayload);

      const response = await billingService.createHostPackage(packagePayload);
      showApiMessage(response, "create package");
      setShowCreatePackageModal(false);
      setPackageCurrentPage(1);
      fetchPackages(1);
      resetPackageForm();
    } catch (error) {
      logger.error("Error creating package:", error);
      setErrorMessage(getApiMessage(error, "create package"));
      showApiMessage(error, "create package");
    } finally {
      setIsLoading(false);
    }
  };

  const resetPackageForm = () => {
    setNewPackage({
      code: "",
      name: "",
      description: "",
      type: "CUSTOM",
      version: "1.0.0",
      discountId: "",
      maximumServiceProvidersAllowed: 1,
      maximumTeamSize: 1,
      currencyId: "", // Start with empty selection
      validity: "",
      validityTimeUnit: "MONTH",
      expiryDate: "",
      recurring: true,
      offerIds: [],
      orgTypeIds: [],
      customerOrgIds: [],
      providerId: 0, // Not used in /me endpoint but kept for update compatibility
    });
    setOfferCurrentPage(1);
    setOfferSearchQuery("");

    // Reset customer org selection
    setCustomerOrgSearchQuery("");
    setCustomerOrgSearchResults([]);
    setShowCustomerOrgSearch(false);

    // Reset original package data
    setOriginalPackageData(null);

    // Reset offer search
    setOfferSearchQuery("");

    // Reload host offers
    fetchPackageDependencies();
  };

  const handleEditPackage = async pkg => {
    try {
      // Show the edit form with loading state
      setShowCreatePackageModal(true);
      setIsLoading(true);

      // Reset form data first
      resetPackageForm();

      // Fetch the latest package data from API
      const response = await billingService.getPackages({ id: pkg.id.toString() });

      if (response.data && response.data.data) {
        const packageData = response.data.data;

        // Store the original package data for comparison during update
        setOriginalPackageData(packageData);

        setNewPackage({
          id: packageData.id,
          code: packageData.code,
          name: packageData.name,
          description: packageData.description,
          type: packageData.type,
          version: packageData.version,
          discountId: packageData.discountId?.toString() || "",
          maximumServiceProvidersAllowed: packageData.maximumServiceProvidersAllowed,
          maximumTeamSize: packageData.maximumTeamSize,
          currencyId: packageData.currencyId?.toString() || "",
          validity: packageData.validity?.toString() || "",
          validityTimeUnit: packageData.validityTimeUnit || "MONTH",
          expiryDate: packageData.expiryDate || "",
          recurring: packageData.recurring,
          offerIds: packageData.offers?.map(offer => offer.id.toString()) || [],
          orgTypeIds: packageData.orgTypes?.map(orgType => orgType.id.toString()) || [],
          // Only load customer organization IDs for CUSTOM packages
          customerOrgIds:
            packageData.type === "CUSTOM" ? packageData.customerOrgs?.map(org => org.id.toString()) || [] : [],
          providerId: packageData.providerId || 0,
        });

        setEditingPackage(packageData);

        // Note: Hosts don't have provider selection - provider is their own organization
        // Reload the available offers (host's offers + universal) with pagination
        setOfferCurrentPage(1);
        await fetchPackageDependencies(1);
      } else {
        throw new Error("Package not found");
      }
    } catch (error) {
      logger.error("Error loading package for edit:", error);
      setErrorMessage(getApiMessage(error, "load package"));
      setShowCreatePackageModal(false); // Close the form if loading fails
    } finally {
      setIsLoading(false); // Hide loading state
    }
  };

  const handleUpdatePackage = async e => {
    e.preventDefault();
    try {
      setIsLoading(true);

      // Validate required fields - MATCH MAIN SCREEN LOGIC
      if (!newPackage.currencyId || newPackage.currencyId === "") {
        setErrorMessage("Please select a currency");
        throw new Error("Please select a currency");
      }

      // Use stored originalPackageData for proper comparison
      if (!originalPackageData) {
        throw new Error("Original package data not found. Please close and reopen the edit form.");
      }

      // Format expiryDate to YYYY-MM-DD if provided
      const formattedExpiryDate = newPackage.expiryDate
        ? new Date(newPackage.expiryDate).toISOString().split("T")[0]
        : undefined;

      // Calculate linked and unlinked org types - MATCH MAIN SCREEN LOGIC
      const originalOrgTypeIds = originalPackageData.orgTypes?.map(orgType => orgType.id) || [];
      const currentOrgTypeIds = newPackage.orgTypeIds.map(id => parseInt(id));
      const linkedOrgTypeIds = currentOrgTypeIds.filter(id => !originalOrgTypeIds.includes(id));
      const unlinkedOrgTypeIds = originalOrgTypeIds.filter(id => !currentOrgTypeIds.includes(id));

      // Calculate linked and unlinked offers - MATCH MAIN SCREEN LOGIC
      const originalOfferIds = originalPackageData.offers?.map(offer => offer.id) || [];
      const currentOfferIds = newPackage.offerIds.map(id => parseInt(id));
      const linkedOfferIds = currentOfferIds.filter(id => !originalOfferIds.includes(id));
      const unlinkedOfferIds = originalOfferIds.filter(id => !currentOfferIds.includes(id));

      // Calculate linked and unlinked customer organizations (only for CUSTOM packages) - MATCH MAIN SCREEN LOGIC
      let linkedCustomerOrgIds = [];
      let unlinkedCustomerOrgIds = [];

      if (originalPackageData.type === "CUSTOM") {
        const originalCustomerOrgIds = originalPackageData.customerOrgs?.map(org => org.id) || [];
        const currentCustomerOrgIds = newPackage.customerOrgIds.map(id => parseInt(id));
        linkedCustomerOrgIds = currentCustomerOrgIds.filter(id => !originalCustomerOrgIds.includes(id));
        unlinkedCustomerOrgIds = originalCustomerOrgIds.filter(id => !currentCustomerOrgIds.includes(id));
      }

      // Note: amount is NOT allowed in UPDATE per API validation (despite being in Swagger)
      const packagePayload = {
        code: newPackage.code,
        name: newPackage.name,
        description: newPackage.description,
        type: "CUSTOM",
        version: newPackage.version,
        maximumServiceProvidersAllowed: newPackage.maximumServiceProvidersAllowed,
        maximumTeamSize: newPackage.maximumTeamSize,
        currencyId: parseInt(newPackage.currencyId),
        // amount is NOT included in UPDATE
        validity: newPackage.recurring ? parseInt(newPackage.validity) || 1 : null,
        validityTimeUnit: newPackage.recurring ? newPackage.validityTimeUnit : null,
        recurring: newPackage.recurring,
        linkedOrgTypeIds,
        unlinkedOrgTypeIds,
        linkedOfferIds,
        unlinkedOfferIds,
        // Only include customer organization linking for CUSTOM packages
        ...(originalPackageData.type === "CUSTOM" && {
          linkedCustomerOrgIds,
          unlinkedCustomerOrgIds,
        }),
      };

      // Add discount if provided
      if (newPackage.discountId) {
        packagePayload.discountId = parseInt(newPackage.discountId);
      }

      // Add expiry date if provided
      if (formattedExpiryDate) {
        packagePayload.expiryDate = formattedExpiryDate;
      }

      logger.log("Update package payload:", packagePayload);

      const response = await billingService.updatePackage(originalPackageData.id, packagePayload);
      showApiMessage(response, "update package");
      setShowCreatePackageModal(false);
      setEditingPackage(null);
      setOriginalPackageData(null);
      setPackageCurrentPage(1);
      fetchPackages(1);
      resetPackageForm();
    } catch (error) {
      logger.error("Error updating package:", error);
      setErrorMessage(getApiMessage(error, "update package"));
      showApiMessage(error, "update package");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePackage = async packageId => {
    const packageItem = packages.find(p => p.id === packageId);
    setPackageToDelete(packageItem);
    setShowDeletePackageDialog(true);
  };

  const confirmDeletePackage = async () => {
    if (!packageToDelete) return;

    try {
      setIsLoading(true);
      const response = await billingService.deletePackage(packageToDelete.id);
      setSuccessMessage("Package deleted successfully!");
      showApiMessage(response, "delete package");
      setPackageCurrentPage(1);
      fetchPackages(1);
    } catch (error) {
      logger.error("Error deleting package:", error);
      setErrorMessage("Failed to delete package");
      showApiMessage(error, "delete package");
    } finally {
      setIsLoading(false);
      setShowDeletePackageDialog(false);
      setPackageToDelete(null);
    }
  };

  const formatDate = dateString => {
    return dateString ? moment(dateString).format("MMM DD, YYYY") : "N/A";
  };

  // Helper function to get default currency code
  const getDefaultCurrencyCode = () => {
    const defaultCurrency = currencies.find(c => c.isDefault === true);
    return defaultCurrency?.code || currencies.find(c => c.code === "USD")?.code || "USD";
  };

  // Helper function to format discount amount
  const formatDiscountAmount = discount => {
    if (discount.percentage) {
      return `${(discount.percentage * 100).toFixed(0)}%`;
    }
    if (discount.fixedValue !== null && discount.fixedValue !== undefined) {
      const currencyCode = getDefaultCurrencyCode();
      return `${currencyCode} ${discount.fixedValue}`;
    }
    return "N/A";
  };

  // Table columns for discounts
  const discountColumns = [
    {
      key: "code",
      header: "Code",
      render: item => <div className="px-2 py-1 font-mono text-sm bg-gray-100 rounded">{item.code}</div>,
      mobileLabel: "Code",
    },
    {
      key: "name",
      header: "Name",
      render: item => (
        <div>
          <div className="font-medium">{item.name}</div>
          <div className="text-sm text-gray-500 truncate max-w-[200px]">{item.description}</div>
        </div>
      ),
      mobileLabel: "Name",
    },
    {
      key: "discount",
      header: "Discount",
      render: item => <div className="font-bold text-green-600">{formatDiscountAmount(item)}</div>,
      mobileLabel: "Discount",
    },
    {
      key: "validity",
      header: "Validity Period",
      render: item => (
        <div className="text-sm">
          <div>{formatDate(item.startDate)}</div>
          <div className="text-gray-500">to {formatDate(item.endDate)}</div>
        </div>
      ),
      mobileLabel: "Validity",
    },
    {
      key: "active",
      header: "Status",
      render: item => (
        <Badge
          variant={item.active ? "default" : "secondary"}
          className={
            item.active ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-100 text-red-800 hover:bg-red-100"
          }
        >
          {item.active ? "Active" : "Inactive"}
        </Badge>
      ),
      mobileLabel: "Status",
    },
    {
      key: "actions",
      header: "Actions",
      render: item => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditDiscount(item)}
            className="h-8 w-8 p-0"
            title="Edit"
          >
            <FaEllipsisH className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteDiscount(item.id)}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-800"
            title="Delete"
          >
            ×
          </Button>
        </div>
      ),
      mobileLabel: "Actions",
    },
  ];

  // Table columns for offers
  const offerColumns = [
    {
      key: "code",
      header: "Code",
      render: item => <div className="px-2 py-1 font-mono text-sm bg-gray-100 rounded">{item.code}</div>,
      mobileLabel: "Code",
    },
    {
      key: "name",
      header: "Name",
      render: item => (
        <div>
          <div className="font-medium">{item.name}</div>
          <div className="text-sm text-gray-500 truncate max-w-[200px]">{item.description}</div>
        </div>
      ),
      mobileLabel: "Name",
    },
    {
      key: "duration",
      header: "Duration",
      render: item => (
        <div className="text-sm">
          <div>
            {item.minimumDuration}-{item.maximumDuration} {item.durationUnit}S
          </div>
        </div>
      ),
      mobileLabel: "Duration",
    },
    {
      key: "serviceTier",
      header: "Service Tier",
      render: item => {
        const getTierVariant = tier => {
          switch (tier) {
            case "HIGH":
              return "bg-purple-100 text-purple-800 hover:bg-purple-100";
            case "MEDIUM":
              return "bg-blue-100 text-blue-800 hover:bg-blue-100";
            default:
              return "bg-gray-100 text-gray-800 hover:bg-gray-100";
          }
        };

        return (
          <Badge variant="secondary" className={getTierVariant(item.serviceTier)}>
            {item.serviceTier}
          </Badge>
        );
      },
      mobileLabel: "Service Tier",
    },
    {
      key: "providers",
      header: "Providers",
      render: item => (
        <div className="text-center">
          <div className="text-lg font-bold">{item.numberOfServiceProviders}</div>
          <div className="text-xs text-gray-500">providers</div>
        </div>
      ),
      mobileLabel: "Providers",
    },
    {
      key: "actions",
      header: "Actions",
      render: item => (
        <div className="flex space-x-2">
          <Button variant="ghost" size="sm" onClick={() => handleEditOffer(item)} className="h-8 w-8 p-0" title="Edit">
            <FaEllipsisH className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteOffer(item.id)}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-800"
            title="Delete"
          >
            ×
          </Button>
        </div>
      ),
      mobileLabel: "Actions",
    },
  ];

  // Table columns for packages
  const packageColumns = [
    {
      key: "code",
      header: "Code",
      render: item => <div className="px-2 py-1 font-mono text-sm bg-gray-100 rounded">{item.code}</div>,
      mobileLabel: "Code",
    },
    {
      key: "name",
      header: "Name",
      render: item => (
        <div>
          <div className="font-medium">{item.name}</div>
          <div className="text-sm text-gray-500 truncate max-w-[200px]">{item.description}</div>
        </div>
      ),
      mobileLabel: "Name",
    },
    {
      key: "pricing",
      header: "Pricing",
      render: item => {
        // Look up currency from currencies array using currencyId
        const currency = currencies.find(c => c.id === item.currencyId || c.id === item.currency?.id);
        const currencyCode = currency?.code || item.currency?.code || "USD";

        return (
          <div className="text-sm">
            <div className="font-bold text-green-600">
              {currencyCode} {item.amount}
            </div>
            <div className="text-gray-500">
              <Badge variant="outline" className="text-xs">
                {item.recurring ? "Recurring" : "One-time"}
              </Badge>
            </div>
          </div>
        );
      },
      mobileLabel: "Pricing",
    },
    {
      key: "features",
      header: "Features",
      render: item => (
        <div className="text-sm">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {item.maximumServiceProvidersAllowed} Providers
            </Badge>
            <Badge variant="outline" className="text-xs">
              {item.maximumTeamSize} Team
            </Badge>
          </div>
        </div>
      ),
      mobileLabel: "Features",
    },
    {
      key: "offers",
      header: "Offers",
      render: item => (
        <div className="text-center">
          <div className="text-lg font-bold">{item.offers?.length || 0}</div>
          <div className="text-xs text-gray-500">offers</div>
        </div>
      ),
      mobileLabel: "Offers",
    },
    {
      key: "actions",
      header: "Actions",
      render: item => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditPackage(item)}
            className="h-8 w-8 p-0"
            title="Edit"
          >
            <FaEllipsisH className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeletePackage(item.id)}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-800"
            title="Delete"
          >
            ×
          </Button>
        </div>
      ),
      mobileLabel: "Actions",
    },
  ];

  return (
    <HPageContainer>
      <HHeader
        rightButton={
          canCreateItems && (
            <HActionButton
              icon={<Plus className="w-4 h-4" />}
              label={`Create ${activeTab.slice(0, -1)}`}
              onClick={() => {
                if (activeTab === "discounts") {
                  setEditingDiscount(null);
                  resetDiscountForm();
                  setShowCreateDiscountModal(true);
                } else if (activeTab === "offers") {
                  setEditingOffer(null);
                  resetOfferForm();
                  setShowCreateOfferModal(true);
                } else if (activeTab === "packages") {
                  setEditingPackage(null);
                  resetPackageForm();
                  setShowCreatePackageModal(true);
                }
              }}
              className="text-xs md:text-sm"
            />
          )
        }
      />
      <div className="flex-1 overflow-auto p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain">
        {/* Tabs */}
        <div className="flex items-center justify-between mb-6 border-b border-gray-200">
          <div className="flex gap-6">
            {["discounts", "offers", "packages"].map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setSearchQuery("");
                  setDebouncedSearchQuery("");
                }}
                className={`pb-3 px-1 text-sm font-medium capitalize transition-colors relative ${
                  activeTab === tab ? "text-blue-600" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
              <Search className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="block py-2 pr-12 pl-10 w-full leading-5 placeholder-gray-500 bg-white rounded-md border border-gray-300 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex justify-between items-center mb-6">
          <ViewToggle activeView={viewMode} onViewChange={setViewMode} availableViews={["list", "grid"]} />
        </div>

        {/* Content based on active tab */}
        {activeTab === "discounts" && (
          <div className="space-y-6">
            {discounts.length === 0 && !isLoading ? (
              <HEmptyState
                icon={Percent}
                title="No discounts found"
                description={
                  canCreateItems
                    ? "Create your first discount to start offering deals to your customers"
                    : "No discounts are available at this time"
                }
                actionLabel={canCreateItems ? "Create Discount" : undefined}
                onAction={canCreateItems ? () => setShowCreateDiscountModal(true) : undefined}
                className="h-64"
              />
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {discounts.map(item => (
                  <div key={item.id} className="bg-white rounded-2xl border-2 border-green-200 p-6 shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <Percent className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">DISCOUNT CODE</div>
                        <div className="font-mono font-bold text-green-700">{item.code}</div>
                      </div>
                      <Badge className={item.active ? "bg-green-600 text-white" : "bg-gray-400 text-white"}>
                        {item.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <h3 className="font-bold text-xl mb-2 text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{item.description}</p>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-4 border border-green-200">
                      <div className="text-5xl font-black text-green-600 text-center">{formatDiscountAmount(item)}</div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-4 pb-4 border-b border-gray-200">
                      <div>
                        <div className="text-gray-400">FROM</div>
                        <div className="font-semibold">{formatDate(item.startDate)}</div>
                      </div>
                      <div className="text-gray-400">→</div>
                      <div>
                        <div className="text-gray-400">TO</div>
                        <div className="font-semibold">{formatDate(item.endDate)}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleEditDiscount(item)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteDiscount(item.id)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {discountColumns.map((col, idx) => (
                        <TableHead key={idx}>{col.header}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {discounts.map(item => (
                      <TableRow key={item.id}>
                        {discountColumns.map((col, idx) => (
                          <TableCell key={idx}>{col.render(item)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {activeTab === "offers" && (
          <div className="space-y-6">
            {offers.length === 0 && !isLoading ? (
              <HEmptyState
                icon={Tag}
                title="No offers found"
                description={
                  canCreateItems
                    ? "Create your first offer to start providing services to your customers"
                    : "No offers are available at this time"
                }
                actionLabel={canCreateItems ? "Create Offer" : undefined}
                onAction={canCreateItems ? () => setShowCreateOfferModal(true) : undefined}
                className="h-64"
              />
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {offers.map(item => (
                  <div key={item.id} className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-lg">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-2">
                        <Tag className="w-5 h-5 text-blue-600" />
                        <span className="text-xs text-gray-500 font-semibold">SERVICE OFFER</span>
                      </div>
                      <Badge
                        className={
                          item.serviceTier === "HIGH"
                            ? "bg-purple-600 text-white"
                            : item.serviceTier === "MEDIUM"
                              ? "bg-blue-600 text-white"
                              : "bg-gray-600 text-white"
                        }
                      >
                        {item.serviceTier}
                      </Badge>
                    </div>
                    <h3 className="font-bold text-2xl mb-2 text-gray-900">{item.name}</h3>
                    <div className="inline-block px-3 py-1 bg-gray-100 rounded-full text-xs font-mono font-semibold text-gray-700 mb-3">
                      {item.code}
                    </div>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[40px]">{item.description}</p>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <span className="text-sm font-medium text-gray-700">Duration Range</span>
                        <span className="text-sm font-bold text-blue-600">
                          {item.minimumDuration}-{item.maximumDuration} {item.durationUnit}S
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                        <span className="text-sm font-medium text-gray-700">Service Providers</span>
                        <span className="text-2xl font-black text-purple-600">{item.numberOfServiceProviders}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4 border-t border-gray-200">
                      <Button variant="outline" size="sm" onClick={() => handleEditOffer(item)} className="flex-1">
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteOffer(item.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {offerColumns.map((col, idx) => (
                        <TableHead key={idx}>{col.header}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {offers.map(item => (
                      <TableRow key={item.id}>
                        {offerColumns.map((col, idx) => (
                          <TableCell key={idx}>{col.render(item)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {activeTab === "packages" && (
          <div className="space-y-6">
            {packages.length === 0 && !isLoading ? (
              <HEmptyState
                icon={Package}
                title="No packages found"
                description={
                  canCreateItems
                    ? "Create your first package to start bundling services for your customers"
                    : "No packages are available at this time"
                }
                actionLabel={canCreateItems ? "Create Package" : undefined}
                onAction={canCreateItems ? () => setShowCreatePackageModal(true) : undefined}
                className="h-64"
              />
            ) : (
              <>
                {viewMode === "grid" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {packages.map((item, index) => {
                      const currency = currencies.find(c => c.id === item.currencyId || c.id === item.currency?.id);
                      const currencyCode = currency?.code || item.currency?.code || "USD";

                      // Dynamic color schemes for packages
                      const colorSchemes = [
                        {
                          bg: "bg-orange-100",
                          border: "border-orange-400",
                          gradient: "from-orange-500 to-amber-500",
                          text: "text-orange-600",
                          badge: "bg-orange-500",
                        },
                        {
                          bg: "bg-blue-100",
                          border: "border-blue-400",
                          gradient: "from-blue-500 to-cyan-500",
                          text: "text-blue-600",
                          badge: "bg-blue-500",
                        },
                        {
                          bg: "bg-purple-100",
                          border: "border-purple-400",
                          gradient: "from-purple-500 to-pink-500",
                          text: "text-purple-600",
                          badge: "bg-purple-500",
                        },
                        {
                          bg: "bg-green-100",
                          border: "border-green-400",
                          gradient: "from-green-500 to-emerald-500",
                          text: "text-green-600",
                          badge: "bg-green-500",
                        },
                        {
                          bg: "bg-red-100",
                          border: "border-red-400",
                          gradient: "from-red-500 to-rose-500",
                          text: "text-red-600",
                          badge: "bg-red-500",
                        },
                        {
                          bg: "bg-indigo-100",
                          border: "border-indigo-400",
                          gradient: "from-indigo-500 to-blue-500",
                          text: "text-indigo-600",
                          badge: "bg-indigo-500",
                        },
                      ];
                      const colors = colorSchemes[index % colorSchemes.length];

                      return (
                        <div key={item.id} className={`bg-white rounded-xl border-2 ${colors.border} p-6 shadow-lg`}>
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                                <Package className={`w-5 h-5 ${colors.text}`} />
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">PACKAGE</div>
                                <div className="font-mono text-sm font-bold text-gray-900">{item.code}</div>
                              </div>
                            </div>
                            <Badge className={`${colors.badge} text-white`}>
                              {item.recurring ? "Recurring" : "One-time"}
                            </Badge>
                          </div>
                          <h3 className="font-black text-2xl mb-2 text-gray-900">{item.name}</h3>
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[40px]">{item.description}</p>
                          <div className="relative mb-4">
                            <div className={`bg-gradient-to-r ${colors.gradient} rounded-xl p-5 text-white shadow-md`}>
                              <div className="text-xs font-semibold mb-1 opacity-90">TOTAL PRICE</div>
                              <div className="text-4xl font-black">
                                {currencyCode} {item.amount}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mb-4">
                            <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="text-2xl font-bold text-gray-900">
                                {item.maximumServiceProvidersAllowed}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">Providers</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="text-2xl font-bold text-gray-900">{item.maximumTeamSize}</div>
                              <div className="text-xs text-gray-500 mt-1">Team Size</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="text-2xl font-bold text-gray-900">{item.offers?.length || 0}</div>
                              <div className="text-xs text-gray-500 mt-1">Offers</div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditPackage(item)}
                              className="flex-1"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeletePackage(item.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {packageColumns.map((col, idx) => (
                            <TableHead key={idx}>{col.header}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {packages.map(item => (
                          <TableRow key={item.id}>
                            {packageColumns.map((col, idx) => (
                              <TableCell key={idx}>{col.render(item)}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {packageTotalCount > packagePageSize && (
                  <div className="mt-8">
                    <HPagination
                      currentPage={packageCurrentPage - 1}
                      totalPages={Math.ceil(packageTotalCount / packagePageSize)}
                      onPageChange={newPage => {
                        setPackageCurrentPage(newPage + 1);
                        fetchPackages(newPage + 1);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Discount Modal */}
      <Dialog open={showCreateDiscountModal} onOpenChange={setShowCreateDiscountModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDiscount ? "Edit Discount" : "Create New Discount"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={editingDiscount ? handleUpdateDiscount : handleCreateDiscount} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="discount-code">Code *</Label>
                <Input
                  id="discount-code"
                  type="text"
                  value={newDiscount.code}
                  onChange={e => handleDiscountInputChange("code", e.target.value)}
                  placeholder="BLACKFRIDAY25"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="discount-name">Name *</Label>
                <Input
                  id="discount-name"
                  type="text"
                  value={newDiscount.name}
                  onChange={e => handleDiscountInputChange("name", e.target.value)}
                  placeholder="Black Friday Special"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="discount-description">Description *</Label>
              <Textarea
                id="discount-description"
                rows={3}
                value={newDiscount.description}
                onChange={e => handleDiscountInputChange("description", e.target.value)}
                placeholder="25% off for Black Friday weekend promotion"
                required
              />
            </div>

            {/* Provider Organization Selection */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h6 className="font-medium text-blue-900 mb-2">Provider Organization Selection *</h6>
              <p className="text-sm text-blue-700 mb-3">
                Search for an organization to set as the provider. This is required for creating discounts.
              </p>

              <div className="mb-3">
                <div className="relative">
                  <input
                    type="text"
                    value={organizationSearchTerm}
                    onChange={e => {
                      setOrganizationSearchTerm(e.target.value);
                      if (e.target.value.length >= 2) {
                        searchOrganizations(e.target.value);
                      } else {
                        setSearchResults([]);
                      }
                    }}
                    placeholder="Search by organization name or code..."
                    className="w-full pl-10 pr-4 py-2 bg-white rounded-md border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-4 h-4" />
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-blue-200 rounded-md bg-white">
                    <div className="p-2 text-xs text-gray-500 border-b border-blue-100">
                      Found {searchResults.length} organizations
                    </div>
                    {searchResults.map(organization => (
                      <div
                        key={organization.id}
                        onClick={() => handleOrganizationSelection(organization)}
                        className="p-3 hover:bg-blue-50 cursor-pointer border-b border-blue-100 last:border-b-0"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <FaUser className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">{organization.name}</div>
                            <div className="text-xs text-gray-600">
                              {organization.code} • {organization.contactEmail || "No email"}
                            </div>
                            <div className="text-xs text-gray-500">{organization.type?.name || "Unknown Type"}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isSearching && <div className="mt-2 text-sm text-blue-600">Searching organizations...</div>}
              </div>

              {/* Selected Organization Display */}
              {selectedOrganization && (
                <div className="p-3 bg-blue-100 rounded-md border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <FaUser className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-blue-900">{selectedOrganization.name}</div>
                        <div className="text-xs text-blue-700">
                          Code: {selectedOrganization.code} (ID: {selectedOrganization.id})
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedOrganization(null);
                        setSelectedProviderId(null);
                        setNewDiscount(prev => ({
                          ...prev,
                          organizationId: null,
                        }));
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {!selectedOrganization && (
                <div className="p-3 bg-red-50 rounded-md border border-red-200">
                  <div className="text-sm text-red-700">
                    <strong>No provider selected.</strong> Please select an organization to continue.
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="discount-percentage">Percentage (%)</Label>
                <Input
                  id="discount-percentage"
                  type="number"
                  min="0"
                  max="100"
                  value={newDiscount.percentage || ""}
                  onChange={e => {
                    handleDiscountInputChange("percentage", e.target.value ? parseInt(e.target.value) : null);
                    if (e.target.value) handleDiscountInputChange("fixedValue", null);
                  }}
                  placeholder="25"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="discount-fixed-value">Fixed Value ({getDefaultCurrencyCode()})</Label>
                <Input
                  id="discount-fixed-value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newDiscount.fixedValue || ""}
                  onChange={e => {
                    handleDiscountInputChange("fixedValue", e.target.value ? parseFloat(e.target.value) : null);
                    if (e.target.value) handleDiscountInputChange("percentage", null);
                  }}
                  placeholder="50.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="discount-start-date">Start Date</Label>
                <Input
                  id="discount-start-date"
                  type="date"
                  value={newDiscount.startDate}
                  onChange={e => handleDiscountInputChange("startDate", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="discount-end-date">End Date</Label>
                <Input
                  id="discount-end-date"
                  type="date"
                  value={newDiscount.endDate}
                  onChange={e => handleDiscountInputChange("endDate", e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="discount-active"
                checked={newDiscount.active}
                onCheckedChange={checked => handleDiscountInputChange("active", checked)}
              />
              <Label htmlFor="discount-active">Active</Label>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateDiscountModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Processing..." : editingDiscount ? "Update Discount" : "Create Discount"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Offer Modal */}
      <Dialog open={showCreateOfferModal} onOpenChange={setShowCreateOfferModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOffer ? "Edit Offer" : "Create New Offer"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={editingOffer ? handleUpdateOffer : handleCreateOffer} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="offer-code">Code *</Label>
                <Input
                  id="offer-code"
                  type="text"
                  value={newOffer.code}
                  onChange={e => handleOfferInputChange("code", e.target.value)}
                  placeholder="PREMIUM-WORKSPACE"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-name">Name *</Label>
                <Input
                  id="offer-name"
                  type="text"
                  value={newOffer.name}
                  onChange={e => handleOfferInputChange("name", e.target.value)}
                  placeholder="Premium Workspace Package"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="offer-description">Description *</Label>
              <Textarea
                id="offer-description"
                rows={3}
                value={newOffer.description}
                onChange={e => handleOfferInputChange("description", e.target.value)}
                placeholder="High-end workspace with all amenities"
                required
              />
            </div>

            {/* Provider Organization Selection */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h6 className="font-medium text-blue-900 mb-2">Provider Organization Selection</h6>
              <p className="text-sm text-blue-700 mb-3">
                Search for an organization to set as the provider. If no provider is selected, the offer will be
                unlinked and available to all organizations.
              </p>

              <div className="mb-3">
                <div className="relative">
                  <input
                    type="text"
                    value={organizationSearchTerm}
                    onChange={e => {
                      setOrganizationSearchTerm(e.target.value);
                      if (e.target.value.length >= 2) {
                        searchOrganizations(e.target.value);
                      } else {
                        setSearchResults([]);
                      }
                    }}
                    placeholder="Search by organization name or code..."
                    className="w-full pl-10 pr-4 py-2 bg-white rounded-md border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-4 h-4" />
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-blue-200 rounded-md bg-white">
                    <div className="p-2 text-xs text-gray-500 border-b border-blue-100">
                      Found {searchResults.length} organizations
                    </div>
                    {searchResults.map(organization => (
                      <div
                        key={organization.id}
                        onClick={() => handleOrganizationSelection(organization)}
                        className="p-3 hover:bg-blue-50 cursor-pointer border-b border-blue-100 last:border-b-0"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <FaUser className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">{organization.name}</div>
                            <div className="text-xs text-gray-600">
                              {organization.code} • {organization.contactEmail || "No email"}
                            </div>
                            <div className="text-xs text-gray-500">{organization.type?.name || "Unknown Type"}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isSearching && <div className="mt-2 text-sm text-blue-600">Searching organizations...</div>}
              </div>

              {/* Selected Organization Display */}
              {selectedOrganization && (
                <div className="p-3 bg-blue-100 rounded-md border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <FaUser className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-blue-900">{selectedOrganization.name}</div>
                        <div className="text-xs text-blue-700">
                          Code: {selectedOrganization.code} (ID: {selectedOrganization.id})
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          {selectedProviderId
                            ? `Will create offer for organization ${selectedProviderId}`
                            : "Will create unlinked offer available to all"}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedOrganization(null);
                        setSelectedProviderId(null);
                        setNewOffer(prev => ({
                          ...prev,
                          organizationId: null,
                          providerId: null,
                        }));
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {!selectedOrganization && (
                <div className="p-3 bg-gray-100 rounded-md border border-gray-200">
                  <div className="text-sm text-gray-700">
                    <strong>No provider selected.</strong> This offer will be unlinked and available to all
                    organizations.
                  </div>
                </div>
              )}
            </div>

            {/* Service Category and Subcategory Selection */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="offer-category">Service Category *</Label>
                <Select
                  value={selectedCategoryId?.toString() || ""}
                  onValueChange={value => {
                    const categoryId = value ? parseInt(value) : null;
                    setSelectedCategoryId(categoryId);
                    if (categoryId) {
                      fetchServiceSubcategories(categoryId);
                    } else {
                      setServiceSubcategories([]);
                    }
                    // Clear subcategory selection when category changes
                    handleOfferInputChange("serviceSubcategoryId", null);
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceCategories.map(category => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name} ({category.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-subcategory">Service Subcategory *</Label>
                <Select
                  value={newOffer.serviceSubcategoryId?.toString() || ""}
                  onValueChange={value =>
                    handleOfferInputChange("serviceSubcategoryId", value ? parseInt(value) : null)
                  }
                  required
                  disabled={!serviceSubcategories.length}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        serviceSubcategories.length === 0
                          ? selectedCategoryId
                            ? "Loading subcategories..."
                            : "Select a category first"
                          : "Select a subcategory"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceSubcategories.map(subcategory => (
                      <SelectItem key={subcategory.id} value={subcategory.id.toString()}>
                        {subcategory.name} ({subcategory.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCategoryId && serviceSubcategories.length === 0 && (
                  <p className="text-xs text-gray-500">No subcategories found for this category</p>
                )}
                {/* Service Subcategory Error */}
                {offerFormErrors.serviceSubcategoryId && (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-200">
                    {offerFormErrors.serviceSubcategoryId}
                  </div>
                )}
              </div>
            </div>

            {/* Package Selection with Provider Compatibility */}
            <div className="space-y-3">
              <Label className="text-base font-medium">
                Packages (Optional - Select packages to link with this offer)
              </Label>

              {packagesLoading ? (
                <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-md border border-gray-200">
                  Loading packages...
                </div>
              ) : packagesError ? (
                <div className="p-4 text-center text-red-500 bg-red-50 rounded-md border border-red-200">
                  {packagesError}
                </div>
              ) : packages.length === 0 ? (
                <div className="p-4 text-center text-gray-400 bg-gray-50 rounded-md border border-gray-200">
                  No packages available
                </div>
              ) : (
                <div className="space-y-3 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                  {packages.map(pkg => {
                    const pkgProviderId = pkg.providerId || pkg.provider?.id || null;
                    const isCompatible = !selectedProviderId || pkgProviderId === selectedProviderId || !pkgProviderId;

                    return (
                      <div key={pkg.id} className="flex items-start space-x-3">
                        <Checkbox
                          id={`package-${pkg.id}`}
                          checked={newOffer.packageIds.includes(pkg.id)}
                          disabled={!isCompatible}
                          onCheckedChange={checked => {
                            if (checked) {
                              // If no provider selected yet and package has provider, set provider
                              if (!selectedProviderId && pkgProviderId) {
                                setSelectedProviderId(pkgProviderId);
                                setNewOffer(prev => ({ ...prev, providerId: pkgProviderId }));
                              }
                              // Add package to selection
                              handleOfferInputChange("packageIds", [...newOffer.packageIds, pkg.id]);
                            } else {
                              // Remove package from selection
                              handleOfferInputChange(
                                "packageIds",
                                newOffer.packageIds.filter(id => id !== pkg.id)
                              );
                            }
                          }}
                        />
                        <div className="flex-1">
                          <label
                            htmlFor={`package-${pkg.id}`}
                            className={`text-sm font-medium cursor-pointer ${!isCompatible ? "text-gray-400" : "text-gray-900"}`}
                          >
                            {pkg.name || pkg.code || pkg.id}
                          </label>
                          <p className="text-xs text-gray-600 mt-1">{pkg.description || "No description"}</p>
                          <div className="text-xs text-gray-500 mt-1 flex gap-2">
                            <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded">{pkg.type}</span>
                            <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded">
                              {(() => {
                                // Look up currency from currencies array using currencyId
                                const currency = currencies.find(
                                  c => c.id === pkg.currencyId || c.id === pkg.currency?.id
                                );
                                const currencyCode = currency?.code || pkg.currency?.code || "USD";
                                return `${currencyCode} ${pkg.amount}`;
                              })()}
                            </span>
                            {!isCompatible && (
                              <span className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded">
                                Incompatible Provider
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-xs text-gray-500">
                Select the packages that should be associated with this offer. Leave empty if no packages are
                associated.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="offer-tier">Service Tier *</Label>
                <Select
                  value={newOffer.serviceTier}
                  onValueChange={value => handleOfferInputChange("serviceTier", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select service tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="offer-currency">Currency *</Label>
                <div className="px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                  {userCurrency ? `${userCurrency.code} - ${userCurrency.name}` : userCurrencyCode || "Loading..."}
                </div>
                <p className="text-xs text-muted-foreground">Currency is based on your country setting</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-price">Unit Price *</Label>
                <Input
                  id="offer-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newOffer.unitPrice}
                  onChange={e => handleOfferInputChange("unitPrice", parseFloat(e.target.value))}
                  placeholder="100.00"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="offer-duration-unit">Duration Unit *</Label>
                <Select
                  value={newOffer.durationUnit}
                  onValueChange={value => handleOfferInputChange("durationUnit", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HOUR">Hour</SelectItem>
                    <SelectItem value="DAY">Day</SelectItem>
                    <SelectItem value="MONTH">Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-min-duration">Min Duration *</Label>
                <Input
                  id="offer-min-duration"
                  type="number"
                  min="1"
                  value={newOffer.minimumDuration}
                  onChange={e => handleOfferInputChange("minimumDuration", parseInt(e.target.value))}
                  placeholder="1"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-max-duration">Max Duration</Label>
                <Input
                  id="offer-max-duration"
                  type="number"
                  min="1"
                  value={newOffer.maximumDuration || ""}
                  onChange={e =>
                    handleOfferInputChange("maximumDuration", e.target.value ? parseInt(e.target.value) : null)
                  }
                  placeholder="8"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="offer-max-checkins">Max Check-ins</Label>
                <Input
                  id="offer-max-checkins"
                  type="number"
                  min="1"
                  value={newOffer.maximumCheckIns || ""}
                  onChange={e =>
                    handleOfferInputChange("maximumCheckIns", e.target.value ? parseInt(e.target.value) : null)
                  }
                  placeholder="5"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="offer-providers">Number of Service Providers *</Label>
                <Input
                  id="offer-providers"
                  type="number"
                  min="1"
                  value={newOffer.numberOfServiceProviders}
                  onChange={e => handleOfferInputChange("numberOfServiceProviders", parseInt(e.target.value))}
                  placeholder="2"
                  required
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateOfferModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Processing..." : editingOffer ? "Update Offer" : "Create Offer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Package Modal */}
      <Dialog
        open={showCreatePackageModal}
        onOpenChange={open => {
          if (open) {
            // When modal opens, reload offers on page 1
            setOfferCurrentPage(1);
            setOfferSearchQuery("");
            fetchPackageDependencies(1);
          } else {
            setShowCreatePackageModal(false);
            resetPackageForm();
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingPackage ? "Edit Package" : "Create New Package"}
            </DialogTitle>
          </DialogHeader>

          {/* Loading spinner for edit form */}
          {editingPackage && isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">Loading package data...</span>
            </div>
          )}

          {/* Show form only when not loading (for edit) or always (for create) */}
          {(!editingPackage || (editingPackage && !isLoading)) && (
            <form
              onSubmit={e => {
                e.preventDefault();
                editingPackage ? handleUpdatePackage(e) : handleCreatePackage(e);
              }}
            >
              <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="package-code" className="text-sm font-medium">
                    Package Code *
                  </Label>
                  <Input
                    id="package-code"
                    type="text"
                    value={newPackage.code}
                    onChange={e => handlePackageInputChange("code", e.target.value)}
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="package-name" className="text-sm font-medium">
                    Package Name *
                  </Label>
                  <Input
                    id="package-name"
                    type="text"
                    value={newPackage.name}
                    onChange={e => handlePackageInputChange("name", e.target.value)}
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="currency" className="text-sm font-medium">
                    Currency *
                  </Label>
                  <Select
                    value={newPackage.currencyId ? newPackage.currencyId.toString() : ""}
                    onValueChange={value => handlePackageInputChange("currencyId", value)}
                    required
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select Currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies && currencies.length > 0 ? (
                        currencies.map(currency => (
                          <SelectItem key={currency.id} value={currency.id.toString()}>
                            {currency.code} - {currency.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="" disabled>
                          Loading currencies...
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {currencies.length === 0 && (
                    <p className="mt-1 text-xs text-red-500">
                      No currencies available. Please check if currencies are configured.
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="max-providers" className="text-sm font-medium">
                    Maximum Service Providers *
                  </Label>
                  <Input
                    id="max-providers"
                    type="number"
                    value={newPackage.maximumServiceProvidersAllowed}
                    onChange={e => handlePackageInputChange("maximumServiceProvidersAllowed", parseInt(e.target.value))}
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="max-team-size" className="text-sm font-medium">
                    Maximum Team Size *
                  </Label>
                  <Input
                    id="max-team-size"
                    type="number"
                    value={newPackage.maximumTeamSize}
                    onChange={e => handlePackageInputChange("maximumTeamSize", parseInt(e.target.value))}
                    className="mt-1"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <Label htmlFor="description" className="text-sm font-medium">
                  Description *
                </Label>
                <Textarea
                  id="description"
                  value={newPackage.description}
                  onChange={e => handlePackageInputChange("description", e.target.value)}
                  rows={3}
                  className="mt-1"
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="discount" className="text-sm font-medium">
                    Discount (Optional)
                  </Label>
                  <Select
                    value={newPackage.discountId || "none"}
                    onValueChange={value => handlePackageInputChange("discountId", value === "none" ? "" : value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="No Discount" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Discount</SelectItem>
                      {getAvailableDiscounts().map(discount => (
                        <SelectItem key={discount.id} value={discount.id.toString()}>
                          {discount.name} ({formatDiscountAmount(discount)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="expiry-date" className="text-sm font-medium">
                    Expiry Date (Optional)
                  </Label>
                  <Input
                    id="expiry-date"
                    type="date"
                    value={newPackage.expiryDate}
                    onChange={e => handlePackageInputChange("expiryDate", e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="validity" className="text-sm font-medium">
                    Validity *
                  </Label>
                  <Input
                    id="validity"
                    type="number"
                    min="1"
                    value={newPackage.validity}
                    onChange={e => handlePackageInputChange("validity", e.target.value)}
                    className="mt-1"
                    placeholder="Enter validity period"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="validity-unit" className="text-sm font-medium">
                    Validity Time Unit
                  </Label>
                  <Select
                    value={newPackage.validityTimeUnit}
                    onValueChange={value => handlePackageInputChange("validityTimeUnit", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select time unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HOUR">Hours</SelectItem>
                      <SelectItem value="DAY">Days</SelectItem>
                      <SelectItem value="MONTH">Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Note: Provider is automatically set to logged-in user's organization for /me endpoint */}
              {/* Provider selection is NOT available for host package creation */}

              {/* Service Offers */}
              <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <Label className="text-sm font-medium text-green-900">Service Offers *</Label>
                <p className="text-sm text-green-700 mb-3">
                  Select offers to include in this package. Available offers are from your organization.
                </p>

                {/* Search Input for Offers */}
                <div className="mb-2">
                  <Input
                    type="text"
                    value={offerSearchQuery}
                    onChange={e => {
                      setOfferSearchQuery(e.target.value);
                      setOfferCurrentPage(1); // Reset to first page on search
                      // Debounce search
                      clearTimeout(window.offerSearchTimeout);
                      window.offerSearchTimeout = setTimeout(() => {
                        searchOffersForPackage(e.target.value, 1);
                      }, 300);
                    }}
                    placeholder="Search offers by name or code..."
                    className="mt-1"
                  />
                </div>

                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-md p-3 bg-white">
                  {availableOffers.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <p className="text-sm">No offers available</p>
                      <p className="text-xs mt-1">Create some offers first to add them to packages</p>
                    </div>
                  ) : (
                    availableOffers.map(offer => {
                      // Get currency info from the offer
                      const currency = currencies.find(c => c.id === offer.currencyId) || offer.currency;
                      const currencyCode = currency?.code || currency?.symbol || "USD";
                      const unitPrice = offer.unitPrice || "0";

                      return (
                        <div key={offer.id} className="flex items-center space-x-2 p-2 rounded-md bg-green-50">
                          <Checkbox
                            id={`offer-${offer.id}`}
                            checked={newPackage.offerIds.includes(offer.id.toString())}
                            onCheckedChange={checked => {
                              if (checked) {
                                handlePackageInputChange("offerIds", [...newPackage.offerIds, offer.id.toString()]);
                              } else {
                                handlePackageInputChange(
                                  "offerIds",
                                  newPackage.offerIds.filter(id => id !== offer.id.toString())
                                );
                              }
                            }}
                          />
                          <Label htmlFor={`offer-${offer.id}`} className="text-sm font-normal cursor-pointer flex-1">
                            <div>
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{offer.name}</span>
                                <span className="font-bold text-green-600 text-sm">
                                  {currencyCode} {unitPrice}
                                </span>
                              </div>
                              <div className="text-muted-foreground text-xs">{offer.description}</div>
                            </div>
                          </Label>
                        </div>
                      );
                    })
                  )}
                </div>
                {offerTotalCount > offerPageSize && (
                  <div className="mt-3 flex justify-center">
                    <Pagination
                      currentPage={offerCurrentPage}
                      totalCount={offerTotalCount}
                      pageSize={offerPageSize}
                      onPageChange={({ selected }) => {
                        setOfferCurrentPage(selected);
                        if (offerSearchQuery.trim()) {
                          searchOffersForPackage(offerSearchQuery, selected);
                        } else {
                          fetchPackageDependencies(selected);
                        }
                      }}
                    />
                  </div>
                )}
                <p className="mt-2 text-xs text-green-600">Showing offers from your organization only.</p>
              </div>

              {/* Customer Organizations Search - Only show for CUSTOM packages */}
              {newPackage.type === "CUSTOM" && (
                <div className="mb-4">
                  <Label className="text-sm font-medium text-gray-700">Customer Organizations (Optional)</Label>

                  {/* Search Input for Customer Orgs */}
                  <div className="mb-2">
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={customerOrgSearchQuery}
                        onChange={e => {
                          setCustomerOrgSearchQuery(e.target.value);
                          // Debounce search
                          clearTimeout(window.customerOrgSearchTimeout);
                          window.customerOrgSearchTimeout = setTimeout(() => {
                            searchCustomerOrganizations(e.target.value);
                          }, 300);
                        }}
                        onFocus={() => setShowCustomerOrgSearch(true)}
                        placeholder="Search organizations by name or code..."
                        className="flex-1 px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowCustomerOrgSearch(!showCustomerOrgSearch)}
                        className="px-3 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        {showCustomerOrgSearch ? "Hide" : "Show"} Search
                      </Button>
                    </div>
                    {isSearchingCustomerOrgs && (
                      <p className="mt-1 text-xs text-blue-600">Searching organizations...</p>
                    )}
                  </div>

                  {/* Search Results Dropdown */}
                  {showCustomerOrgSearch && customerOrgSearchResults.length > 0 && (
                    <div className="mb-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg">
                      {customerOrgSearchResults.map(org => (
                        <div
                          key={org.id}
                          onClick={() => addCustomerOrganization(org)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{org.name}</p>
                              <p className="text-xs text-gray-500">
                                {org.code} • ID: {org.id}
                              </p>
                            </div>
                            <span className="text-xs text-blue-600">Add</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Selected Organizations List */}
                  {newPackage.customerOrgIds.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-1">Selected Organizations:</p>
                      <div className="flex flex-wrap gap-1">
                        {newPackage.customerOrgIds.map(orgId => {
                          const org = customerOrgSearchResults.find(u => u.id.toString() === orgId) || {
                            id: orgId,
                            name: "Organization",
                            code: orgId,
                          };
                          return (
                            <span
                              key={orgId}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                            >
                              {org.name} ({org.code})
                              <button
                                type="button"
                                onClick={() => removeCustomerOrganization(orgId)}
                                className="ml-1 text-blue-600 hover:text-blue-800"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <p className="mt-1 text-xs text-gray-500">
                    Search and select specific customer organizations that can access this package. Leave empty for all
                    organizations.
                  </p>
                </div>
              )}

              <div className="flex items-center mb-4">
                <Checkbox
                  id="recurring"
                  checked={newPackage.recurring}
                  onCheckedChange={checked => handlePackageInputChange("recurring", checked)}
                />
                <Label htmlFor="recurring" className="text-sm font-medium ml-2 cursor-pointer">
                  Recurring Billing
                </Label>
              </div>

              <DialogFooter className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreatePackageModal(false);
                    resetPackageForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading} variant="default">
                  {isLoading ? "Saving..." : !editingPackage ? "Create Package" : "Update Package"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialogs */}
      <HConfirmDialog
        show={showDeleteDiscountDialog}
        onClose={() => {
          setShowDeleteDiscountDialog(false);
          setDiscountToDelete(null);
        }}
        onConfirm={confirmDeleteDiscount}
        isLoading={isLoading}
        title="Delete Discount"
        message={`Are you sure you want to delete the discount "${discountToDelete?.name || "this discount"}"? This action cannot be undone.`}
        confirmText="Delete Discount"
        cancelText="Cancel"
        confirmVariant="destructive"
      />

      <HConfirmDialog
        show={showDeleteOfferDialog}
        onClose={() => {
          setShowDeleteOfferDialog(false);
          setOfferToDelete(null);
        }}
        onConfirm={confirmDeleteOffer}
        isLoading={isLoading}
        title="Delete Offer"
        message={`Are you sure you want to delete the offer "${offerToDelete?.name || "this offer"}"? This action cannot be undone.`}
        confirmText="Delete Offer"
        cancelText="Cancel"
        confirmVariant="destructive"
      />

      <HConfirmDialog
        show={showDeletePackageDialog}
        onClose={() => {
          setShowDeletePackageDialog(false);
          setPackageToDelete(null);
        }}
        onConfirm={confirmDeletePackage}
        isLoading={isLoading}
        title="Delete Package"
        message={`Are you sure you want to delete the package "${packageToDelete?.name || "this package"}"? This action cannot be undone.`}
        confirmText="Delete Package"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </HPageContainer>
  );
};

export default OffersDiscounts;
